import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
  WellInformationSection,
  type WellTableSelectionDelta,
} from './well-information-section';
import { Button } from '@carbon/react';
import { InlineEdit } from 'components/inline-edit';
import { PageHeader } from 'components/page-header';
import { ExportImageDrawer } from 'components/chart-export';
import request from 'lib/request';
import type { DcaWorkspaceResponse } from 'types/dca-workspaces';
import styles from './dca-workspace-content.module.scss';
import { DeclineCurveAnalysis } from './decline-curve-analysis';
import { WellboreMap } from './wellbore-map';
import { ProbitPlots } from './probit-plots';
import { useDcaWorkspaceWells, fetchAllFilteredUwis } from './hooks';
import {
  syncWellSelectionForecasts,
  wellsIndexingState,
  WELLS_INDEXING_BANNERS,
} from './utils';
import {
  workspaceWellSelectionMapAtomFamily,
  batchRegressionStatusAtom,
  activeRegressionRequestAtom,
  isWellSelected,
  countSelectedWells,
  buildWellSelectionQueryFilter,
  mapVisibleUwisAtom,
  dcaModalForecastSavedAtom,
  promoteWellsMutation,
} from './atoms';
import { selectedDcaScenarioAtom } from 'components/well-page-content/atoms';
import type { WellSelectionFilterMode } from './well-selection-filter';
import { usePolling } from 'lib/hooks/usePolling';
import { ActiveJob } from './active-job';
import { FilterCards, type FilterCardId } from './filter-cards';
import { buildMapTileFilterParams } from './build-workspace-wells-query-payload';
import { AtomisedNotifications } from 'components/atomised-notifications';
import {
  addNotificationsAtom,
  clearNotificationGroupAtom,
  NotificationGroup,
} from 'components/atomised-notifications/atoms';
import { Rocket, Download } from '@carbon/react/icons';
import { formatNumber } from 'utils/number';
import { useWorkspaceExport } from './use-workspace-export';

type Props = {
  workspace: DcaWorkspaceResponse;
};

const EMPTY_SET = new Set<string>();

export const DcaWorkspaceContent: FC<Props> = ({ workspace }) => {
  // const [patchError, setPatchError] = useState<string | null>(null);
  const [visibleChartUwis, setVisibleChartUwis] =
    useState<Set<string>>(EMPTY_SET);
  const [selectionFilterMode, setSelectionFilterMode] =
    useState<WellSelectionFilterMode>('all');
  const [editedName, setEditedName] = useState(workspace.name ?? '');
  const addNotifications = useSetAtom(addNotificationsAtom);
  const clearNotificationGroup = useSetAtom(clearNotificationGroupAtom);

  const [wellsMap, setWellsMap] = useAtom(
    workspaceWellSelectionMapAtomFamily(workspace.id)
  );
  const [promoteWells, setPromoteWells] = useAtom(promoteWellsMutation);

  const selectedScenario = useAtomValue(selectedDcaScenarioAtom);
  const [regressionStatus, setRegressionStatus] = useAtom(
    batchRegressionStatusAtom
  );
  const fetchActiveRequest = useSetAtom(activeRegressionRequestAtom);
  const postRegressionRef = useRef(false);

  useEffect(() => {
    if (workspace.hasActiveRegressionRequest) {
      void fetchActiveRequest(workspace.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const wellsHook = useDcaWorkspaceWells({
    workspaceId: workspace.id,
    dcaScenarioId: selectedScenario?.id ?? null,
  });

  const fetchWellsRef = useRef(wellsHook.fetchWells);
  fetchWellsRef.current = wellsHook.fetchWells;

  const [indexingState, setIndexingState] = useState(() =>
    wellsIndexingState(workspace)
  );

  useEffect(() => {
    // The group holds one banner at a time, and adding never replaces, so the
    // previous one has to go first.
    clearNotificationGroup(NotificationGroup.DcaWorkspaceIndexing);
    if (indexingState === 'done') return;

    addNotifications(NotificationGroup.DcaWorkspaceIndexing, [
      {
        id: `indexing-${indexingState}-${workspace.id}`,
        ...WELLS_INDEXING_BANNERS[indexingState],
        hideCloseButton: true,
      },
    ]);
  }, [indexingState, workspace.id, addNotifications, clearNotificationGroup]);

  // Keeps checking after indexing turns slow or failed, so wells that index late
  // still load. Once it has failed, a late finish is rare, so it checks less often.
  const checkIndexingStatus = useCallback(async () => {
    const res = await request.get(workspace.href);
    if (res && typeof res === 'object' && 'hasWellsIndexingInProgress' in res) {
      const next = wellsIndexingState(res as DcaWorkspaceResponse);
      setIndexingState(next);
      if (next === 'done') wellsHook.fetchWells();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspace.href, wellsHook.fetchWells]);

  usePolling({
    fn: checkIndexingStatus,
    intervalMs: 5_000,
    enabled: indexingState === 'in_progress' || indexingState === 'slow',
    immediate: false,
  });
  usePolling({
    fn: checkIndexingStatus,
    intervalMs: 60_000,
    enabled: indexingState === 'failed',
    immediate: false,
  });

  const [dcaModalSavedUwi, setDcaModalSavedUwi] = useAtom(
    dcaModalForecastSavedAtom
  );
  const modalSavedUwiRef = useRef<string | null>(null);

  // While this is set, we are waiting an arbitrary delay for reindexing to finish before refetching.
  const waitingForForecastSaveIndexingRef = useRef(false);

  useEffect(() => {
    if (!dcaModalSavedUwi) return;
    modalSavedUwiRef.current = dcaModalSavedUwi;
    waitingForForecastSaveIndexingRef.current = true;

    const timer = window.setTimeout(
      () =>
        fetchWellsRef.current().finally(() => {
          waitingForForecastSaveIndexingRef.current = false;
          setDcaModalSavedUwi(null);
        }),
      2000 // TODO: wait for reindexing to finish
    );

    return () => {
      window.clearTimeout(timer);
      waitingForForecastSaveIndexingRef.current = false;
    };
  }, [dcaModalSavedUwi, setDcaModalSavedUwi]);

  useEffect(() => {
    if (regressionStatus !== 'success') return;
    postRegressionRef.current = true;
    setRegressionStatus('idle');
    wellsHook.fetchWells();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regressionStatus, setRegressionStatus, wellsHook.fetchWells]);

  useEffect(() => {
    if (!wellsHook.wells?.length) return;

    const isPostRegression = postRegressionRef.current;
    postRegressionRef.current = false;

    const savedUwi = modalSavedUwiRef.current;
    if (!waitingForForecastSaveIndexingRef.current) {
      modalSavedUwiRef.current = null;
    }

    setWellsMap((prev) =>
      syncWellSelectionForecasts(prev, wellsHook.wells, {
        savedUwi,
        isPostRegression,
      })
    );
  }, [wellsHook.wells, setWellsMap]);

  const handleWellSelectedChange = useCallback(
    (uwi: string, selected: boolean) => {
      setWellsMap((prev) => {
        const existing = prev.items[uwi];
        return {
          ...prev,
          items: {
            ...prev.items,
            [uwi]: {
              selected,
              dcaForecastId: existing?.dcaForecastId ?? '',
              isLatestForecast: existing?.isLatestForecast ?? true,
            },
          },
        };
      });
    },
    [setWellsMap]
  );

  const handleSelectionChange = useCallback(
    ({ selected, deselected }: WellTableSelectionDelta) => {
      if (selected.length === 0 && deselected.length === 0) return;
      setWellsMap((prev) => {
        const items = { ...prev.items };
        for (const uwi of selected) {
          const existing = items[uwi];
          items[uwi] = {
            selected: true,
            dcaForecastId: existing?.dcaForecastId ?? '',
            isLatestForecast: existing?.isLatestForecast ?? true,
          };
        }
        for (const uwi of deselected) {
          const existing = items[uwi];
          items[uwi] = {
            selected: false,
            dcaForecastId: existing?.dcaForecastId ?? '',
            isLatestForecast: existing?.isLatestForecast ?? true,
          };
        }
        return { ...prev, items };
      });
    },
    [setWellsMap]
  );

  const isTableFilterScoped = useMemo(
    () =>
      wellsHook.columnFilters.length > 0 ||
      wellsHook.globalFilter.trim() !== '' ||
      wellsHook.wellsMissingForecast === true ||
      wellsHook.wellsInEarlyTime === true ||
      wellsHook.wellsUpdated === true ||
      (wellsHook.uwis?.length ?? 0) > 0 ||
      (wellsHook.excludeUwis?.length ?? 0) > 0 ||
      wellsHook.meta.total < wellsHook.cardFilters.totalWells,
    [wellsHook]
  );

  const bulkSelectionAbortRef = useRef<AbortController | null>(null);

  const handleBulkSelectionChange = useCallback(
    (selectAll: boolean) => {
      bulkSelectionAbortRef.current?.abort();

      if (!isTableFilterScoped) {
        setWellsMap((prev) => {
          const mode = selectAll ? 'all_selected' : 'not_selected';
          const items = { ...prev.items };
          for (const uwi of Object.keys(items)) {
            items[uwi] = { ...items[uwi], selected: selectAll };
          }
          return { mode, items };
        });
        return;
      }

      const controller = new AbortController();
      bulkSelectionAbortRef.current = controller;

      void fetchAllFilteredUwis(
        {
          workspaceId: workspace.id,
          dcaScenarioId: selectedScenario?.id ?? null,
          text: wellsHook.globalFilter,
          columnFilters: wellsHook.columnFilters,
          uwis: wellsHook.uwis,
          excludeUwis: wellsHook.excludeUwis,
          wellsMissingForecast: wellsHook.wellsMissingForecast,
          wellsInEarlyTime: wellsHook.wellsInEarlyTime,
          wellsUpdated: wellsHook.wellsUpdated,
        },
        controller
      )
        .then((filteredUwis) => {
          if (controller.signal.aborted) return;
          setWellsMap((prev) => {
            const items = { ...prev.items };
            for (const uwi of filteredUwis) {
              const existing = items[uwi];
              items[uwi] = {
                selected: selectAll,
                dcaForecastId: existing?.dcaForecastId ?? '',
                isLatestForecast: existing?.isLatestForecast ?? true,
              };
            }
            return { ...prev, items };
          });
        })
        .catch(() => {
          /* ignore — superseded fetch or transient error */
        });
    },
    [
      isTableFilterScoped,
      setWellsMap,
      workspace.id,
      selectedScenario?.id,
      wellsHook.globalFilter,
      wellsHook.columnFilters,
      wellsHook.uwis,
      wellsHook.excludeUwis,
      wellsHook.wellsMissingForecast,
      wellsHook.wellsInEarlyTime,
      wellsHook.wellsUpdated,
    ]
  );

  const DEFAULT_FILTERS = useMemo(() => new Set<FilterCardId>(['total']), []);
  const [activeFilterCards, setActiveFilterCards] =
    useState<Set<FilterCardId>>(DEFAULT_FILTERS);

  const hasTableFilters =
    wellsHook.columnFilters.length > 0 || wellsHook.globalFilter !== '';

  useEffect(() => {
    if (hasTableFilters) {
      setActiveFilterCards(new Set());
    }
  }, [hasTableFilters]);

  const filterCardCounts = useMemo(() => {
    const cardFilters = wellsHook.cardFilters;
    const selected = countSelectedWells(wellsMap, cardFilters.totalWells);
    return {
      total: cardFilters.totalWells,
      selected,
      wellsNotSelected: cardFilters.totalWells - selected,
      wellsMissingForecast: cardFilters.wellsMissingForecast,
      wellsInEarlyTime: cardFilters.wellsInEarlyTime,
      wellsUpdated: cardFilters.wellsUpdated,
    };
  }, [wellsMap, wellsHook.cardFilters]);

  const handleFilterCardClick = useCallback(
    (id: FilterCardId) => {
      const isSelectionCard =
        id === 'total' || id === 'selected' || id === 'wellsNotSelected';

      if (isSelectionCard) {
        if (activeFilterCards.has(id) && !hasTableFilters) return;
        setActiveFilterCards(new Set([id]));
        wellsHook.resetFilters();

        if (id === 'total') return;

        const selectionFilter = buildWellSelectionQueryFilter(wellsMap);
        const filter =
          id === 'selected'
            ? selectionFilter
            : {
                uwis: selectionFilter.excludeUwis,
                excludeUwis: selectionFilter.uwis,
              };
        if (filter.uwis || filter.excludeUwis) {
          wellsHook.onSelectionFilterChange(filter);
        }
        return;
      }

      // Clicking any of these three filter cards toggles between filtering to
      // them and the unfiltered view.
      const next = activeFilterCards.has(id) ? 'total' : id;
      setActiveFilterCards(new Set([next]));
      wellsHook.onSelectionFilterChange({});
      wellsHook.onMissingForecastChange(
        next === 'wellsMissingForecast' ? true : undefined
      );
      wellsHook.onEarlyTimeWellChange(
        next === 'wellsInEarlyTime' ? true : undefined
      );
      wellsHook.onWellsUpdatedChange(
        next === 'wellsUpdated' ? true : undefined
      );
    },
    [activeFilterCards, wellsMap, wellsHook, hasTableFilters]
  );
  const mapVisibleUwis = useAtomValue(mapVisibleUwisAtom);

  const mapFilterParams = useMemo(
    () =>
      buildMapTileFilterParams(
        wellsHook.columnFilters,
        wellsHook.globalFilter,
        {
          dcaScenarioId: selectedScenario?.id,
          wellsMissingForecast: wellsHook.wellsMissingForecast,
          wellsInEarlyTime: wellsHook.wellsInEarlyTime,
          wellsUpdated: wellsHook.wellsUpdated,
        }
      ),
    [
      wellsHook.columnFilters,
      wellsHook.globalFilter,
      selectedScenario?.id,
      wellsHook.wellsMissingForecast,
      wellsHook.wellsInEarlyTime,
      wellsHook.wellsUpdated,
    ]
  );

  const highlightedRowIds = useMemo(() => {
    if (mapVisibleUwis.length === 0 && visibleChartUwis.size === 0) {
      return EMPTY_SET;
    }
    const combined = new Set(visibleChartUwis);
    for (const uwi of mapVisibleUwis) combined.add(uwi);
    return combined;
  }, [visibleChartUwis, mapVisibleUwis]);

  const filteredWells = useMemo(() => {
    if (selectionFilterMode === 'selected') {
      return wellsHook.wells.filter((w) => isWellSelected(wellsMap, w.uwi));
    }
    if (selectionFilterMode === 'not_selected') {
      return wellsHook.wells.filter((w) => !isWellSelected(wellsMap, w.uwi));
    }
    return wellsHook.wells;
  }, [wellsHook.wells, wellsMap, selectionFilterMode]);

  const handleSaveName = useCallback(
    (newName: string) => {
      if (newName === workspace.name) return;
      void (async () => {
        clearNotificationGroup(NotificationGroup.DcaWorkspaceUpdate);
        const ac = new AbortController();
        const res = await request.patch<DcaWorkspaceResponse, unknown>(
          workspace.href,
          { name: newName, notes: workspace.notes ?? '' },
          ac
        );
        if (res.type === 'success') return;
        if (res.type === 'error' && res.data && typeof res.data === 'object') {
          const errBody = res.data as { errors?: Array<{ message?: string }> };
          const messages = errBody.errors
            ?.map((e) => e.message)
            .filter(Boolean);
          addNotifications(NotificationGroup.DcaWorkspaceUpdate, [
            {
              id: 'patch-error',
              kind: 'error',
              title: messages?.length
                ? messages.join(', ')
                : 'Could not update workspace name',
              message: 'Could not update workspace name',
            },
          ]);
        } else {
          addNotifications(NotificationGroup.DcaWorkspaceUpdate, [
            {
              id: 'patch-error',
              kind: 'error',
              title: 'Could not update workspace name',
              message: 'Could not update workspace name',
            },
          ]);
        }
      })();
    },
    [
      workspace.href,
      workspace.notes,
      workspace.name,
      addNotifications,
      clearNotificationGroup,
    ]
  );

  const exportFilters = useMemo(
    () => ({
      text: wellsHook.globalFilter,
      columnFilters: wellsHook.columnFilters,
      uwis: wellsHook.uwis,
      wellsMissingForecast: wellsHook.wellsMissingForecast,
      wellsInEarlyTime: wellsHook.wellsInEarlyTime,
    }),
    [
      wellsHook.globalFilter,
      wellsHook.columnFilters,
      wellsHook.uwis,
      wellsHook.wellsMissingForecast,
      wellsHook.wellsInEarlyTime,
    ]
  );

  const workspaceExport = useWorkspaceExport({
    workspaceId: workspace.id,
    dcaScenarioId: selectedScenario?.id ?? null,
    wellsMap,
    totalWells: wellsHook.cardFilters.totalWells,
    filters: exportFilters,
  });

  useEffect(() => {
    const { status, error, rowCount } = workspaceExport.exportStatus;
    if (status !== 'complete' && status !== 'failed') return;

    clearNotificationGroup(NotificationGroup.DcaWorkspaceExport);

    if (status === 'failed') {
      addNotifications(NotificationGroup.DcaWorkspaceExport, [
        {
          id: 'workspace-export-error',
          kind: 'error',
          title: 'Export failed',
          message: error ?? 'Could not export the selected wells',
        },
      ]);
      return;
    }

    if (rowCount === 0) {
      addNotifications(NotificationGroup.DcaWorkspaceExport, [
        {
          id: 'workspace-export-empty',
          kind: 'warning',
          title: 'Nothing to export',
          message: 'The selected wells have no forecasts.',
        },
      ]);
      return;
    }

    const wellCount = workspaceExport.exportedWellCount;
    addNotifications(NotificationGroup.DcaWorkspaceExport, [
      {
        id: 'workspace-export-success',
        kind: 'success',
        title: `Exported ${formatNumber({
          value: wellCount,
          minPrecision: 0,
          maxPrecision: 0,
        })} well${wellCount === 1 ? '' : 's'}`,
        message: 'Your download should start automatically.',
      },
    ]);
  }, [
    workspaceExport.exportStatus,
    workspaceExport.exportedWellCount,
    addNotifications,
    clearNotificationGroup,
  ]);

  const handlePromoteWells = () => {
    if (!selectedScenario?.id) return;

    const wellsWithSpecificVersionIds: {
      uwi: string;
      wellDcaForecastId: number;
    }[] = [];
    const specificUwis = new Set<string>();

    for (const [uwi, entry] of Object.entries(wellsMap.items)) {
      if (entry.selected && !entry.isLatestForecast && entry.dcaForecastId) {
        wellsWithSpecificVersionIds.push({
          uwi,
          wellDcaForecastId: Number(entry.dcaForecastId),
        });
        specificUwis.add(uwi);
      }
    }

    const payload: {
      dcaScenarioId: number;
      excludeUwis?: string[];
      uwis?: string[];
      wellsWithSpecificVersionIds?: {
        uwi: string;
        wellDcaForecastId: number;
      }[];
    } = {
      dcaScenarioId: selectedScenario.id,
      ...buildWellSelectionQueryFilter(wellsMap),
    };

    if (wellsWithSpecificVersionIds.length > 0) {
      payload.wellsWithSpecificVersionIds = wellsWithSpecificVersionIds;
    }

    clearNotificationGroup(NotificationGroup.DcaWorkspacePromoteWells);
    setPromoteWells({
      type: 'POST',
      url: `/dca_workspaces/${workspace.id}/promote_forecasts`,
      data: payload,
      onSuccess: (data) => {
        const count = data?.promotedCount ?? 0;
        addNotifications(NotificationGroup.DcaWorkspacePromoteWells, [
          {
            id: 'promote-wells-success',
            kind: 'success',
            title: 'Success',
            message: `${formatNumber({
              value: count,
              minPrecision: 0,
              maxPrecision: 0,
            })} well${count === 1 ? '' : 's'} promoted successfully`,
          },
        ]);
        wellsHook.fetchWells();
      },
      onError: () => {
        addNotifications(NotificationGroup.DcaWorkspacePromoteWells, [
          {
            id: 'promote-wells-error',
            kind: 'error',
            title: 'Error',
            message: 'Could not promote wells',
          },
        ]);
      },
    });
  };

  return (
    <>
      <PageHeader
        breadcrumbs={[]}
        title={
          <span className={styles.workspaceTitle}>
            <InlineEdit
              id={`dca-workspace-${workspace.id}-name`}
              className={styles.inlineEdit}
              value={editedName}
              onChange={(val: string) => setEditedName(val)}
              onCancel={(initialVal: string) => {
                setEditedName(initialVal);
                // v11 bug: cancel button doesn't exit edit mode (no setFocused(false)).
                // Force blur to trigger the component's onBlurHandler which exits edit mode.
                requestAnimationFrame(() => {
                  if (document.activeElement instanceof HTMLElement) {
                    document.activeElement.blur();
                  }
                });
              }}
              labelText="DCA Workspace name"
              editDescription="Edit workspace name"
              saveDescription="Save"
              cancelDescription="Cancel"
              editAlwaysVisible
              inheritTypography
              onSave={() => handleSaveName(editedName)}
              placeholder="Workspace name"
              size="md"
            />
          </span>
        }
        actions={
          <div className={styles.headerActions}>
            <Button
              className={styles.promoteButton}
              kind="tertiary"
              size="md"
              onClick={workspaceExport.startExport}
              disabled={
                workspaceExport.isExporting ||
                workspaceExport.selectedCount === 0
              }
              renderIcon={Download}
            >
              {workspaceExport.isExporting ? 'Exporting...' : 'Export'}
            </Button>
            <Button
              className={styles.promoteButton}
              kind="primary"
              size="md"
              onClick={handlePromoteWells}
              disabled={promoteWells.loading}
              renderIcon={Rocket}
            >
              Promote
            </Button>
          </div>
        }
      />
      {/* Row so the export drawer compresses the content column from the RIGHT
          (full height) instead of stacking below it. */}
      <div className={styles.contentRow}>
        <div className={styles.pageBody}>
          <AtomisedNotifications
            groupIds={[
              NotificationGroup.DcaWorkspaceIndexing,
              NotificationGroup.DcaWorkspacePromoteWells,
              NotificationGroup.DcaWorkspaceExport,
              NotificationGroup.DcaWorkspaceUpdate,
            ]}
          />

          <FilterCards
            activeFilters={activeFilterCards}
            onFilterClick={handleFilterCardClick}
            counts={filterCardCounts}
          />

          {/* wellbore */}
          <WellboreMap
            workspaceId={workspace.id}
            filterParams={mapFilterParams}
            highlightedUwis={highlightedRowIds}
          />

          {/* probit plots */}
          <ProbitPlots
            workspaceId={workspace.id}
            dcaScenarioId={selectedScenario?.id}
            wellsMap={wellsMap}
            columnFilters={wellsHook.columnFilters}
            globalFilter={wellsHook.globalFilter}
          />

          <ActiveJob />

          {/* decline curve analysis */}
          <DeclineCurveAnalysis
            wells={filteredWells}
            dcaWorkspaceId={workspace.id}
            defaultDcaScenarioId={workspace.dcaScenarioId}
            wellsMap={wellsMap}
            loading={wellsHook.loading}
            refetchWells={wellsHook.fetchWells}
            onWellSelectedChange={handleWellSelectedChange}
            onVisibleUwisChange={setVisibleChartUwis}
          />

          {/* well information data table */}

          <div className={styles.panel}>
            <section
              className={styles.panel}
              aria-labelledby="well-table-section-heading"
            >
              <div className={styles.staticPanelHeader}>
                <h2 className={styles.sectionHeading}>
                  Well Information Data Table ({wellsHook.meta.total} Wells)
                </h2>
              </div>
              <div className={styles.sectionBody}>
                <WellInformationSection
                  workspaceId={workspace.id}
                  wellsHook={wellsHook}
                  wellsMap={wellsMap}
                  onSelectionChange={handleSelectionChange}
                  onBulkSelectionChange={handleBulkSelectionChange}
                  highlightedRowIds={highlightedRowIds}
                  selectionFilterMode={selectionFilterMode}
                  onSelectionFilterModeChange={setSelectionFilterMode}
                />
              </div>
            </section>
          </div>
        </div>
        <ExportImageDrawer />
      </div>
    </>
  );
};
